import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GallerySlider from "components/GallerySlider/GallerySlider";
import SaleOffBadge from "components/SaleOffBadge/SaleOffBadge";
import returnMoodColor from "shared/AppFunctions/returnMoodColor";
import Badge from "shared/Badge/Badge";
import BtnLikeIcon from "components/Buttons/BtnLikeIcon";
import { formatPrice } from "shared/AppFunctions/formatPrice";
import type { MoodEnum } from "data/enums";

export interface MultiStockCardVerticalProps {
  id: string | number;
  className?: string;
  cover: string;
  images?: string[];
  title: string;
  annotation?: string;
  mood?: MoodEnum;
  description?: string;
  price?: number;
  salePrice?: number;
  currency?: string;
  rating?: number;
  address?: string;
  reviewCount?: number;
  max_guests?: number;
  minutes_duration?: number;
  isLiked?: boolean;
  onClickLike?: (e: any) => void;
  href?: string;
  children?: React.ReactNode | React.ReactNode[];
  draggable?: boolean;
  onDragStart?: (e: any) => void;
  onDragEnd?: (e: any) => void;
  in_review?: boolean;
  stopped?: boolean;
  showStatusBadge?: boolean;
};

const MultiStockCardVertical: FC<MultiStockCardVerticalProps> = ({
  id,
  className = "",
  mood = null,
  cover,
  images = [],
  annotation = null,
  title,
  description = null,
  price,
  salePrice,
  currency = "EUR",
  rating,
  address = null,
  reviewCount,
  max_guests = null,
  minutes_duration = null,
  isLiked,
  onClickLike,
  href,
  draggable = false,
  onDragStart = () => { },
  onDragEnd = () => { },
  children,
  in_review = false,
  stopped = false,
  showStatusBadge = false,
}) => {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const formattedPrice = price ? formatPrice(price, true, window.navigator.language, currency) : null;
  const formattedSalePrice = salePrice ? formatPrice(salePrice, true, window.navigator.language, currency) : null;

  let galleryImages = [];

  if (cover) {
    galleryImages = [cover];
    if (images && images.length > 0) {
      galleryImages = [...galleryImages, ...images];
    }
  } else if (images && images.length > 0) {
    galleryImages = images;
  }

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full h-0 aspect-w-4 aspect-h-3 overflow-hidden">
        <GallerySlider
          uniqueID={`MultiStockCardVertical_${id}`}
          ratioClass="aspect-w-4 aspect-h-3 "
          galleryImgs={galleryImages}
        />
        <div className="absolute left-3 top-3 flex flex-col gap-2 max-w-[70%]">
          {
            formattedSalePrice && <SaleOffBadge />
          }
          {
            in_review && (
              <div className="bg-amber-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-max">
                <i className="las la-hourglass-half text-xs"></i>
                {t("translation:myListings.badge.inReview")}
              </div>
            )
          }
          {
            showStatusBadge && stopped && (
              <div className="bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-max">
                <i className="las la-pause-circle text-xs"></i>
                {t("translation:myListings.badge.paused")}
              </div>
            )
          }
          {
            showStatusBadge && !stopped && !in_review && (
              <div className="bg-emerald-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-max">
                <i className="las la-check-circle text-xs"></i>
                {t("translation:myListings.badge.active")}
              </div>
            )
          }
        </div>
        {
          reviewCount && reviewCount > 0 && <BtnLikeIcon isLiked={isLiked} className="absolute right-3 top-3 z-[1]" />
        }
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="p-4 space-y-2">
        {
          annotation && <div className="text-sm text-neutral-500 dark:text-neutral-400">{annotation}</div>
        }
        <div className="flex space-x-2">
          <div className="flex items-center space-x-2">
            {mood && <Badge name={mood} color={returnMoodColor(mood)} />}
          </div>
          <h3
            className={`font-medium capitalize text-lg`}
          >
            <span className="line-clamp-1">{title}</span>
          </h3>
        </div>
        { description && <p className="text-sm text-neutral-600">{`${description.length < 100 ? description : description.substring(0, 100) + "..."}`}</p> }
        {
          address && (
            <div className="flex items-center text-neutral-500 dark:text-neutral-400 text-sm space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            <span className="">{address}</span>
          </div>
          )
        }

        {(minutes_duration || max_guests) && (
          <div className="flex items-center space-x-4 text-sm text-neutral-500 dark:text-neutral-400">
            {minutes_duration && (
              <div className="flex items-center space-x-1">
                <i className="las la-clock"></i>
                <span>{minutes_duration} minuti</span>
              </div>
            )}
            {max_guests && (
              <div className="flex items-center space-x-1">
                <i className="las la-user"></i>
                <span>Fino a {max_guests} persone</span>
              </div>
            )}
          </div>
        )}

        { formattedPrice && (
          <div className="w-14 border-b border-neutral-100 dark:border-neutral-800"></div>
        )}

        {
          formattedPrice && (
            <div className="mt-2">
              <span className="text-lg font-bold">{
                formattedSalePrice ? formattedSalePrice : formattedPrice
              }</span>
              {
                formattedSalePrice && <span className="text-sm line-through">{formattedPrice}</span>
              }
            </div>
          )
        }

        { children && (
          <div className="w-14 border-b border-neutral-100 dark:border-neutral-800"></div>
        )}

        <div className="flex justify-end gap-2 items-center">
          { children }
        </div>
      </div>
    );
  };

  return (
    <div
      className={`cursor-pointer group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden will-change-transform hover:shadow-xl transition-shadow ${className}`}
      data-nc-id="MultiStockCardVertical"
      data-uuid={id}
      data-title={title}
      draggable={draggable}
      onDragStart={(e) => {
        if (draggable && onDragStart) {
          onDragStart(e);
        }
      }}
      onDragEnd={(e) => {
        if (draggable && onDragEnd) {
          onDragEnd(e);
        }
      }}
      key={`MultiStockCardVertical_${id}`}
    >
      <div>
        {
          renderSliderGallery()
        }
        <div onClick={() => {
          if (href) {
            navigate(href);
          }
        }}>
          {
            renderContent()
          }
        </div>
      </div>
    </div>
  );
};

export default MultiStockCardVertical;
